"use client";

import {
    createContext,
    useContext,
    useMemo,
    type ReactNode,
} from "react";
import {
    convertCurrencyAmount,
    type ConvertCurrencyAmountOptions,
} from "@/lib/currency";
import {
    MONEY_DISPLAY_PRESET_CODES,
    normalizeMoneyCurrencyCode,
    type MoneyDisplayPresetCode,
} from "@/lib/moneyCurrencyCode";

/** Any valid ISO 4217 code (presets: {@link MONEY_DISPLAY_PRESET_CODES}). */
export type SupportedCurrencyCode = string;

export type MoneyFormatContextValue = {
    baseCurrency: string;
    currency: string;
    locale: string;
    setCurrency: (currency: SupportedCurrencyCode) => void;
    /** Полная строка с валютой (Intl), напр. «50 ₼» или «50,00 USD». */
    format: (amount: number) => string;
};

const MoneyFormatContext = createContext<MoneyFormatContextValue | null>(null);

function fractionPolicyForCode(code: string): {
    normalizedAmount: (n: number) => number;
    maxFractionDigits: number;
} {
    const c = code.toUpperCase();
    if (c === "EUR" || c === "RUB") {
        return { normalizedAmount: (n) => Math.round(n), maxFractionDigits: 0 };
    }
    return { normalizedAmount: (n) => n, maxFractionDigits: 2 };
}

function buildFormat(
    baseCurrency: string,
    currency: string,
    locale: string,
    convertOptions?: ConvertCurrencyAmountOptions,
): (amount: number) => string {
    const base = (baseCurrency || "AZN").toUpperCase();
    const code = (currency || "AZN").toUpperCase();
    const loc = locale || "en";
    const { normalizedAmount, maxFractionDigits } = fractionPolicyForCode(code);
    return (amount: number) => {
        const convertedAmount = convertCurrencyAmount(
            amount,
            base,
            code,
            convertOptions,
        );
        const normalized = normalizedAmount(convertedAmount);
        try {
            return new Intl.NumberFormat(loc, {
                style: "currency",
                currency: code,
                currencyDisplay: "narrowSymbol",
                minimumFractionDigits: 0,
                maximumFractionDigits: maxFractionDigits,
            }).format(normalized);
        } catch {
            return `${normalized} ${code}`;
        }
    };
}

const fallbackValue: MoneyFormatContextValue = {
    baseCurrency: "AZN",
    currency: "AZN",
    locale: "en",
    setCurrency: () => {},
    format: buildFormat("AZN", "AZN", "en", undefined),
};

export function MoneyFormatProvider({
    baseCurrency,
    currency,
    locale,
    setCurrency,
    rubPerAzn,
    eurPerAzn,
    children,
}: {
    baseCurrency: string;
    currency: string;
    locale: string;
    setCurrency: (currency: SupportedCurrencyCode) => void;
    /** CBAR-backed RUB per 1 AZN for display; omit to use static fallback for AZN↔RUB. */
    rubPerAzn?: number | null;
    /** CBAR-backed EUR per 1 AZN for display; omit to use static fallback for AZN↔EUR. */
    eurPerAzn?: number | null;
    children: ReactNode;
}) {
    const value = useMemo((): MoneyFormatContextValue => {
        const baseNorm =
            normalizeMoneyCurrencyCode(baseCurrency) ?? "AZN";
        const codeNorm = normalizeMoneyCurrencyCode(currency) ?? "AZN";
        const loc = locale || "en";
        const convertOptions: ConvertCurrencyAmountOptions | undefined =
            rubPerAzn === undefined && eurPerAzn === undefined
                ? undefined
                : { rubPerAzn, eurPerAzn };
        return {
            baseCurrency: baseNorm,
            currency: codeNorm,
            locale: loc,
            setCurrency,
            format: buildFormat(baseNorm, codeNorm, loc, convertOptions),
        };
    }, [baseCurrency, currency, locale, setCurrency, rubPerAzn, eurPerAzn]);

    return (
        <MoneyFormatContext.Provider value={value}>
            {children}
        </MoneyFormatContext.Provider>
    );
}

/** Форматирование денег в UI; при отсутствии провайдера — AZN/en. */
export function useMoneyFormat(): MoneyFormatContextValue {
    return useContext(MoneyFormatContext) ?? fallbackValue;
}

export { MONEY_DISPLAY_PRESET_CODES, type MoneyDisplayPresetCode };
